import { useApi, SKY_API_ENDPOINT } from '@/api';
import {
  DataTable,
  Flex,
  Card,
  CryptoIcon,
  formatCurrency,
  useIsMobile,
  formatPercentage,
  Box,
  NetworkIcon,
  ProtocolIcon,
  Text,
  useTableProps
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { InfoTooltip, TableSkeleton } from '@/components';

type StarRequiredRiskCapitalTableProps = {
  star: string;
};

export default function StarRequiredRiskCapitalTable({
  star
}: StarRequiredRiskCapitalTableProps) {
  const isMobile = useIsMobile();
  const { pagination, sorting } = useTableProps({
    pageSize: 20,
    initialSorting: '-exposure'
  });

  const [data, error] = useApi({
    baseUrl: SKY_API_ENDPOINT,
    url: `/rrc/primes/${star}/allocations/`,
    queryParams: {
      sort: sorting.currentSorting,
      page: pagination.currentPage,
      limit: pagination.pageSize
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  const columns: TableColumn[] = [
    {
      header: '',
      getCell: (row) => (
        <Flex alignItems="center" gap="3xs">
          <NetworkIcon name={row.network} size="2" />
          <ProtocolIcon protocol={row.protocol} size="2" />
          {row.loan_token_symbol && (
            <CryptoIcon
              name={row.loan_token_symbol}
              address={row.loan_token_address}
              network={row.network}
              size="2"
            />
          )}
        </Flex>
      ),
      columnSize: isMobile ? '70px' : '100px'
    },
    {
      header: 'Asset',
      getCell: (row) => (
        <Flex flexDirection="column" gap="4xs">
          <Box as="span">{row.symbol}</Box>
          <Box as="span" color="muted" fontSize="-2">
            {row.name}
          </Box>
        </Flex>
      )
    },
    {
      header: 'Exposure',
      getCell: (row) => formatCurrency(row.exposure),
      sortingField: 'exposure'
    },
    {
      header: (
        <Flex gap="3xs">
          Financial RRC
          <InfoTooltip
            text={
              <Text size="3">
                Financial Required Risk Capital is the risk capital requirement
                for financial risk.
              </Text>
            }
          />
        </Flex>
      ),
      getCell: (row) => formatCurrency(row.rrc),
      sortingField: 'rrc'
    },
    {
      header: '% of Exposure',
      getCell: (row) => formatPercentage(row.crr),
      sortingField: 'crr'
    }
  ];

  return (
    <Card>
      <DataTable
        rows={data.data.results}
        columns={columns}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.data.pagination.total
        }}
        getKey={(row) =>
          `${row.protocol}${row.network}${row.wallet_address}${row.token_address}${row.loan_token_address}`
        }
      />
    </Card>
  );
}
